import { saveAs } from 'file-saver';

import { FormDoc, FormQuestion } from '@vyce/core/src/types';

/**
 * Opens the browser print dialog (used for “Export PDF” on form analysis views).
 */
export function printFormAsPdf(): void {
  window.print();
}

const sanitizeFileName = (name: string): string =>
  name.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'document.pdf';

const ensurePdfExtension = (name: string): string =>
  /\.pdf$/i.test(name) ? name : `${name}.pdf`;

const buildPdfFileName = (question: FormQuestion, fallbackIndex: number): string => {
  const attachmentName = question.pdf?.name?.trim();
  if (attachmentName) return ensurePdfExtension(sanitizeFileName(attachmentName));
  const titlePart = sanitizeFileName(question.title || `question-${fallbackIndex + 1}`);
  return `${titlePart}.pdf`;
};

export const formHasPdfDocs = (form: FormDoc | null | undefined): boolean =>
  !!form?.questions?.some(question => !!question.pdf?.url);

export async function downloadFormPdfDocs(form: FormDoc | null | undefined): Promise<void> {
  if (!form?.questions?.length) return;

  const pdfQuestions = form.questions.filter(question => !!question.pdf?.url);
  const usedNames = new Set<string>();

  for (let index = 0; index < pdfQuestions.length; index++) {
    const question = pdfQuestions[index];
    const url = question.pdf?.url;
    if (!url) continue;

    let fileName = buildPdfFileName(question, index);
    if (usedNames.has(fileName)) {
      const dotIndex = fileName.lastIndexOf('.');
      const base = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
      const ext = dotIndex > 0 ? fileName.slice(dotIndex) : '';
      fileName = `${base} (${index + 1})${ext}`;
    }
    usedNames.add(fileName);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      saveAs(blob, fileName);
    } catch (error) {
      console.error(`Failed to download PDF for question "${question.title}"`, error);
    }
  }
}
